import { Country_Enum } from "@/graphql";

/**
 * FE-only guidance copy for the calculation-type choice in the start-project dialog (RTLCA-5377).
 *
 * Keyed by the type's `label` — not its id — because `calculationType` carries no description column on
 * Hasura, only `label`, and adding one is a schema change outside this ticket's scope. Label-keying is
 * therefore fragile on purpose rather than by accident: rename a label on Hasura, or in a Storybook
 * fixture, and the matching entry below silently stops applying instead of erroring. If a marker or a
 * note goes missing where it used to show, check the row's `label` first.
 */

/**
 * The calculation type worth marking "common choice" in the dropdown, one per country.
 *
 * A country absent from this map — Germany, Iceland, the Faroe Islands and Greenland today — gets no
 * marker at all. There is no single obviously-right type for them yet, and guessing one would be worse
 * than saying nothing.
 */
const COMMON_CALCULATION_TYPE_LABEL_BY_COUNTRY: Partial<Record<Country_Enum, string>> = {
  [Country_Enum.DENMARK]: "BR18 (2025)",
  [Country_Enum.SWEDEN]: "Klimatdeklaration",
  [Country_Enum.FINLAND]: "Climate Declaration 2026-2028",
};

export const commonCalculationTypeLabelFor = (country: Country_Enum | null | undefined): string | undefined =>
  country ? COMMON_CALCULATION_TYPE_LABEL_BY_COUNTRY[country] : undefined;

/**
 * One plain-language sentence per calculation type, for a reader who has never heard of "BR18 (2025)" or
 * "Klimatdeklaration". Absent on purpose for any label not listed here — an incomplete list is safer than
 * a wrong or stale sentence.
 */
const CALCULATION_TYPE_NOTE_BY_LABEL: Record<string, string> = {
  "BR18 (2025)":
    "Denmark's building regulation requirements from 2025, the method most new Danish buildings are assessed against.",
  "BR18 (2023)":
    "The 2023 version of Denmark's building regulation requirements, for projects permitted under the earlier rules.",
  "Low emission (2025)":
    "Denmark's voluntary low-emission class, with stricter limit values than the standard 2025 requirements.",
  Custom:
    "You set up the assessment yourself and choose which life-cycle stages to include, for projects that don't follow a specific regulation.",
  Klimatdeklaration: "Sweden's statutory climate declaration for new buildings.",
  "Climate Declaration 2026-2028": "Finland's climate declaration with the limit values that apply from 2026 to 2028.",
  "Climate Declaration 2029": "Finland's climate declaration with the stricter limit values that apply from 2029.",
  "Level(s)": "The EU's voluntary framework for assessing sustainable buildings, with no fixed limit value.",
};

export const calculationTypeNoteFor = (label: string | null | undefined): string | undefined =>
  label ? CALCULATION_TYPE_NOTE_BY_LABEL[label] : undefined;
